import type { UserRole } from '@/types/domain';

/**
 * Permission catalogue. UI checks permissions (never roles directly) so the role
 * matrix can change in one place. Mirrors RLS policies in the migration.
 */
export type Permission =
  | 'tender.view'
  | 'tender.create'
  | 'tender.edit'
  | 'tender.close'
  | 'quantity.record'
  | 'quantity.edit'
  | 'quantity.delete'
  | 'lineItem.edit'
  | 'lineItem.delete'
  | 'financial.edit'
  | 'financial.delete'
  | 'financial.record'
  | 'document.upload'
  /** Amendments apply to the contract immediately; only managers and administrators can create them. */
  | 'amendment.create'
  | 'vendor.manage'
  | 'report.export'
  | 'audit.view'
  | 'user.manage'
  | 'settings.manage'
  | 'tender.delete'
  | 'tender.assignOwner'
  | 'alert.manage'
  /** Approve / reject an additional-budget request (managers and administrators). */
  | 'budget.decide';

const ALL: Permission[] = [
  'tender.view', 'tender.create', 'tender.edit', 'tender.close', 'tender.delete', 'quantity.record', 'quantity.edit', 'quantity.delete', 'lineItem.edit', 'lineItem.delete', 'financial.record', 'financial.edit', 'financial.delete',
  'document.upload', 'amendment.create', 'vendor.manage',
  'report.export', 'audit.view', 'user.manage', 'settings.manage', 'tender.assignOwner', 'alert.manage', 'budget.decide',
];

export const ROLE_PERMISSIONS: Record<UserRole, Permission[]> = {
  admin: ALL,
  manager: ALL.filter((p) => p !== 'user.manage' && p !== 'settings.manage' && p !== 'tender.delete'),
  editor: ['tender.view', 'tender.create', 'tender.edit', 'quantity.record', 'quantity.edit', 'lineItem.edit', 'financial.record', 'financial.edit', 'document.upload', 'report.export'],
  viewer: ['tender.view', 'report.export'],
  auditor: ['tender.view', 'report.export', 'audit.view'],
};

export const ROLE_LABELS: Record<UserRole, string> = {
  admin: 'Administrator',
  manager: 'Manager',
  editor: 'Editor',
  viewer: 'Viewer',
  auditor: 'Auditor',
};

export const ROLE_DESCRIPTIONS: Record<UserRole, string> = {
  admin: 'Full access including users and system settings',
  manager: 'Amend contracts, close contracts, manage vendors',
  editor: 'Create and update tenders and transactions',
  viewer: 'Read-only access to tenders and reports',
  auditor: 'Read-only access plus the complete audit trail',
};

export function roleHas(role: UserRole | undefined, permission: Permission): boolean {
  return !!role && ROLE_PERMISSIONS[role].includes(permission);
}
